import { accountsMappings } from './accounts/accounts.mappings';
import { addressMappings } from './addresses/addresses.mappings';
import { analyticsMappings } from './analytics/analytics.mappings';
import { appliancesMappings } from './appliances/appliances.mappings';
import { automationsMappings } from './automations/automations.mappings';
import { chatAgentMappings } from './chatAgent/chatAgent.mappings';
import { contactMappings } from './contacts/contacts.mappings';
import { customersMappings } from './customers/customers.mappings';
import { dashboardMappings } from './dashboard/dashboard.mappings';
import { dealsMappings } from './deals/deals.mappings';
import type {
  AllSystemRoles,
  SusbcriptionFeatureName,
} from './default-role.config';
import {
  expiredTrialRoles,
  roleInheritanceMappings,
  rolesBySubscriptionFeatureNameAndSystemRole,
  SubscriptionLevels,
} from './default-role.config';
import { dispatchMappings } from './dispatch/dispatch.mappings';
import { documentInstanceMappings } from './documentInstance/documentInstance.mappings';
import { documentsMappings } from './documents/documents.mappings';
import { documentTemplateMappings } from './documentTemplate/documentTemplate.mappings';
import { enquiryFormsMappings } from './enquiryForms/enquiryForms.mappings';
import { estimatesMappings } from './estimates/estimates.mappings';
import { grantsMappings } from './grants/grants.mappings';
import { inboundEmailsMappings } from './inbound-emails/inbound-emails.mappings';
import { integratedEmailsMappings } from './integrated-emails/integrated-emails.mappings';
import { invoicesMappings } from './invoices/invoices.mappings';
import { jobMappings } from './jobs/job.mappings';
import { labourForecastMappings } from './labourForecast/labourForecast.mappings';
import { lineItemsMappings } from './lineItems/line-items.mappings';
import { materialsMappings } from './materials/materials.mappings';
import { materialsListMappings } from './materialsList/materialsList.mappings';
import { paymentsMappings } from './payments/payments.mappings';
import { pipelinesMappings } from './pipelines/pipelines.mappings';
import { proposalsMappings } from './proposals/proposals.mappings';
import { scheduledEventsMappings } from './scheduledEvents/scheduled-events.mappings';
import { servicePlanMappings } from './service-plans/service-plans.mappings';
import { stockLocationsMappings } from './stockLocations/stockLocations.mappings';
import { suppliersMappings } from './suppliers/suppliers.mappings';
import { tasksMappings } from './tasks/tasks.mappings';
import { taxRateMappings } from './tax-rates/tax-rates.mappings';
import { templatesMappings } from './templates/templates.mappings';
import { timelogsMappings } from './timelogs/timelogs.mappings';
import { uploadsMappings } from './uploads/uploads.mappings';
import { urlsMappings } from './urls/urls.mappings';
import { userMappings } from './user/user.mappings';
import { userAssignmentsMappings } from './userAssignments/userAssignments.mappings';

const PermissionRoleMap: any = {
  ...jobMappings,
  ...accountsMappings,
  ...analyticsMappings,
  ...chatAgentMappings,
  ...customersMappings,
  ...dashboardMappings,
  ...dealsMappings,
  ...documentsMappings,
  ...jobMappings,
  ...lineItemsMappings,
  ...scheduledEventsMappings,
  ...tasksMappings,
  ...invoicesMappings,
  ...paymentsMappings,
  ...pipelinesMappings,
  ...automationsMappings,
  ...suppliersMappings,
  ...stockLocationsMappings,
  ...materialsMappings,
  ...appliancesMappings,
  ...uploadsMappings,
  ...materialsListMappings,
  ...proposalsMappings,
  ...taxRateMappings,
  ...userAssignmentsMappings,
  ...servicePlanMappings,
  ...dispatchMappings,
  ...enquiryFormsMappings,
  ...estimatesMappings,
  ...grantsMappings,
  ...templatesMappings,
  ...timelogsMappings,
  ...inboundEmailsMappings,
  ...integratedEmailsMappings,
  ...userMappings,
  ...contactMappings,
  ...addressMappings,
  ...urlsMappings,
  ...labourForecastMappings,
  ...documentTemplateMappings,
  ...documentInstanceMappings,
};

export const getUserRoles = (
  role: AllSystemRoles,
  features: {
    subscriptionFeatureNames?: Array<SusbcriptionFeatureName>;
  },
  isExpired = false
) => {
  const roles = [
    ...(roleInheritanceMappings[SubscriptionLevels.GROWTH]?.[role] || []),
  ];
  features.subscriptionFeatureNames?.forEach((name) => {
    const x = rolesBySubscriptionFeatureNameAndSystemRole[name]?.[role];
    if (x) {
      roles.push(...(x as any)); // vomit
    }
  });
  return isExpired
    ? roles.filter((role) => expiredTrialRoles.includes(role as any))
    : roles;
};

export const userHasRequiredPermission = (
  userRoles: string[],
  permissions: string[]
) => {
  // we'll loop through the required permissions for this endpoint to ensure our user has a role compatible with AT LEAST ONE of the required permissions (doesn't need all!)
  return !!permissions.find((permission: string) => {
    // the PermissionRoleMap gives an array of roles that will grant access, keyed by permission - we'll check our roles against this array to see if we have a suitable match
    return !!(PermissionRoleMap[permission] || []).find(
      (allowableRole: string) => {
        // is this role within our userRoles? - if so we're good to progress
        return userRoles.includes(allowableRole);
      }
    );
  });
};

export const getPermissionsFromRoles = (roles: string[]): string[] => {
  const permissions: string[] = [];

  Object.entries(PermissionRoleMap as Record<string, string[]>).forEach(
    ([key, value]) => {
      const hasRole = !!value.find((role) => roles.includes(role));
      if (hasRole) {
        permissions.push(key);
      }
    }
  );

  return permissions;
};

export const userHasAllRequiredPermissions = (
  userRoles: string[],
  permissions: string[]
) =>
  permissions.every((permission) => {
    const requiredRoles: Array<string> = PermissionRoleMap[permission] || [];
    return requiredRoles.every((role) => userRoles.includes(role));
  });

export const getUserSubscriptionLevelFromSubscriptionMapping = (
  userSubscriptionMapping?: SubscriptionLevels
) => userSubscriptionMapping || SubscriptionLevels.CORE;

export const userHasRequiredSubscription = (
  userSubscriptionProduct: any,
  minimumAllowableSubscription: SubscriptionLevels
) => {
  // are they on a trial?
  if (!userSubscriptionProduct) return SubscriptionLevels.BUSINESS;
  // evaluate their subscription level
  const userSubscriptionLevel = getUserSubscriptionLevelFromSubscriptionMapping(
    userSubscriptionProduct?.mapping
  );

  switch (minimumAllowableSubscription) {
    case SubscriptionLevels.BUSINESS:
      return userSubscriptionLevel === SubscriptionLevels.BUSINESS;
    case SubscriptionLevels.TEAM:
      return [SubscriptionLevels.TEAM, SubscriptionLevels.BUSINESS].includes(
        userSubscriptionLevel
      );
    default:
      return true;
  }
};
